import React, { useState, useEffect, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Paper,
  IconButton,
  Breadcrumbs,
  Link,
  Tabs,
  Tab,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Photo as PhotoIcon, ArrowBack as ArrowBackIcon, Delete as DeleteIcon, CloudUpload, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import customFrameService from '../../services/customFrameService';
import { userFrameService, UserFrameChannel } from '../../services/userFrameService';
import { CustomFrame } from '../../types';
import { rewriteToCdn } from '../../utils/cdn';
import CustomFrameLayoutPreview from '../shared/CustomFrameLayoutPreview';
import SlotAspectCropDialog from '../shared/SlotAspectCropDialog';

// Interface cho User Frame
interface UserFrame {
  _id: string;
  name: string;
  description: string;
  frameImage: {
    url: string;
    path: string;
  };
  tags: string[];
  isActive: boolean;
  metadata: {
    originalFileName: string;
    fileSize: number;
    uploadDate: string;
  };
  createdAt: string;
  updatedAt: string;
}

// Interface cho API response
interface UserFramesResponse {
  success: boolean;
  userFrames: UserFrame[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

interface BackgroundsResponse {
  success: boolean;
  backgrounds: UserFrame[];
}

interface CustomFrameDetailProps {
  channel?: UserFrameChannel;
}

const CustomFrameDetail: React.FC<CustomFrameDetailProps> = ({ channel = 'pro' }) => {
  const { customFrameId } = useParams<{ customFrameId: string }>();
  const [customFrame, setCustomFrame] = useState<CustomFrame | null>(null);
  const [userFrames, setUserFrames] = useState<UserFrame[]>([]);
  const [backgrounds, setBackgrounds] = useState<UserFrame[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUploadingFrames, setIsUploadingFrames] = useState(false);
  const [isUploadingBackgrounds, setIsUploadingBackgrounds] = useState(false);
  const [bgCropOpen, setBgCropOpen] = useState(false);
  const [bgCropFile, setBgCropFile] = useState<File | null>(null);
  const [activeTab, setActiveTab] = useState<'frames' | 'backgrounds'>('frames');
  const navigate = useNavigate();
  const backPath = channel === 'lite' ? '/m-photo-lite?tab=custom-frames' : '/m-photo-pro?proTab=custom-frames';

  useEffect(() => {
    if (customFrameId) {
      loadCustomFrameAndUserFrames();
    }
  }, [customFrameId, channel]);

  const loadCustomFrameAndUserFrames = async () => {
    try {
      setLoading(true);
      
      // Load custom frame
      const customFrameResponse = await customFrameService.getCustomFrameById(customFrameId!);
      // rewrite CDN cho frame ảnh gốc
      setCustomFrame({
        ...customFrameResponse.frame,
        frameImage: customFrameResponse.frame.frameImage
          ? { ...customFrameResponse.frame.frameImage, url: rewriteToCdn(customFrameResponse.frame.frameImage.url) }
          : customFrameResponse.frame.frameImage
      });
      
      // Load user frames + backgrounds
      await loadUserFrames();
      await loadBackgrounds();
      
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load custom frame');
      toast.error('Failed to load custom frame');
    } finally {
      setLoading(false);
    }
  };

  const loadUserFrames = async () => {
    try {
      const data: UserFramesResponse = await userFrameService.getUserFrames({
        customFrameId,
        limit: 200,
        channel,
      });
      // rewrite CDN cho user frames
      const frames = (data.userFrames || []).map(f => ({
        ...f,
        frameImage: f.frameImage ? { ...f.frameImage, url: rewriteToCdn(f.frameImage.url) } : f.frameImage
      }));
      setUserFrames(frames);
    } catch (error) {
      console.error('Error loading user frames:', error);
      setUserFrames([]);
    }
  };

  const loadBackgrounds = async () => {
    try {
      const data: BackgroundsResponse = await userFrameService.getBackgroundsByCustomFrame(customFrameId!, channel);
      const items = (data.backgrounds || []).map(bg => ({
        ...bg,
        frameImage: bg.frameImage ? { ...bg.frameImage, url: rewriteToCdn(bg.frameImage.url) } : bg.frameImage
      }));
      setBackgrounds(items);
    } catch (error) {
      console.error('Error loading backgrounds:', error);
      setBackgrounds([]);
    }
  };

  const getSizeColor = (size: string) => {
    const colors: { [key: string]: string } = {
      '2x4': '#2196f3',
      '4x2': '#f44336',
      '2x6': '#4caf50',
      '6x2': '#ff9800',
      '4x6': '#9c27b0',
      '6x4': '#607d8b',
      '3x4': '#e91e63',
    };
    return colors[size] || '#757575';
  };

  const handleDeleteUserFrame = async (userFrameId: string) => {
    try {
      await userFrameService.deleteUserFrame(userFrameId, channel);

      // Xóa user frame khỏi state
      setUserFrames(prev => prev.filter(frame => frame._id !== userFrameId));
      toast.success('User frame deleted successfully');
    } catch (error) {
      console.error('Error deleting user frame:', error);
      toast.error('Failed to delete user frame');
    }
  };

  const handleDeleteBackground = async (backgroundId: string) => {
    try {
      await userFrameService.deleteBackground(backgroundId, channel);
      setBackgrounds(prev => prev.filter(item => item._id !== backgroundId));
      toast.success('Background deleted successfully');
    } catch (error) {
      console.error('Error deleting background:', error);
      toast.error('Failed to delete background');
    }
  };

  const validateUploadFiles = (files: File[]) => {
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error(`File ${file.name} không phải là file ảnh (JPEG, PNG)`);
        return false;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`File ${file.name} vượt quá giới hạn 5MB`);
        return false;
      }
    }
    return true;
  };

  const uploadUserFrames = async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;
    if (!validateUploadFiles(acceptedFiles)) return;

    try {
      setIsUploadingFrames(true);

      const result = await userFrameService.uploadUserFrames(customFrameId!, acceptedFiles, channel);

      if (result.success) {
        await loadUserFrames();
        toast.success(`Đã upload thành công ${result.createdFrames?.length || 0} frames`);
      } else {
        throw new Error(result.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload frame error:', error);
      toast.error(error instanceof Error ? error.message : 'Upload failed');
    } finally {
      setIsUploadingFrames(false);
    }
  };

  const firstSlotSize = useMemo(() => {
    const slot = customFrame?.photoPositions?.[0];
    const w = Number(slot?.width) || Number(customFrame?.width) || 1080;
    const h = Number(slot?.height) || Number(customFrame?.height) || 1440;
    return { width: Math.max(1, Math.round(w)), height: Math.max(1, Math.round(h)) };
  }, [customFrame]);

  const uploadCroppedBackground = async (croppedFile: File) => {
    try {
      setIsUploadingBackgrounds(true);
      const result = await userFrameService.uploadBackgrounds(customFrameId!, [croppedFile], channel);
      if (result.success) {
        await loadBackgrounds();
        toast.success('Đã upload background');
        setBgCropOpen(false);
        setBgCropFile(null);
      } else {
        throw new Error(result.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload background error:', error);
      toast.error(error instanceof Error ? error.message : 'Upload failed');
      throw error;
    } finally {
      setIsUploadingBackgrounds(false);
    }
  };

  const { getRootProps: getBackgroundRootProps, getInputProps: getBackgroundInputProps, isDragActive: isBackgroundDragActive } = useDropzone({
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp']
    },
    maxFiles: 1,
    multiple: false,
    maxSize: 50 * 1024 * 1024,
    disabled: isUploadingBackgrounds,
    onDrop: async (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (!file) return;
      if (!validateUploadFiles([file])) return;
      if (!customFrame?.photoPositions?.[0]) {
        toast.error('Khung chưa có ô slot — không thể căn tỉ lệ nền');
        return;
      }
      setBgCropFile(file);
      setBgCropOpen(true);
    }
  });

  const { getRootProps: getFrameRootProps, getInputProps: getFrameInputProps, isDragActive: isFrameDragActive } = useDropzone({
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png']
    },
    maxFiles: 10,
    maxSize: 50 * 1024 * 1024,
    onDrop: uploadUserFrames
  });

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!customFrame) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          Không tìm thấy custom frame
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(backPath)}
          sx={{ mb: 2 }}
        >
          Quay lại
        </Button>
        
         <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
           <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
             {customFrame.name}
           </Typography>
           {customFrame.link && (
             <Link
               href={customFrame.link}
               target="_blank"
               rel="noopener noreferrer"
               sx={{
                 display: 'inline-flex',
                 alignItems: 'center',
                 gap: 1,
                 textDecoration: 'none',
                 color: 'white',
                 backgroundColor: 'primary.main',
                 padding: '8px 16px',
                 borderRadius: '8px',
                 fontWeight: 500,
                 fontSize: '0.875rem',
                 boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                 transition: 'all 0.2s ease-in-out',
                 '&:hover': {
                   backgroundColor: 'primary.dark',
                   boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                   transform: 'translateY(-1px)',
                 },
               }}
             >
               <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
                 Đường dẫn thiết kế
               </Typography>
               <OpenInNewIcon fontSize="small" />
             </Link>
           )}
         </Box>
         
         {/* Layout preview — vẽ ô slot từ photoPositions (không dùng ảnh URL) */}
         <Box
           sx={{
             mb: 3,
             display: 'flex',
             justifyContent: 'center',
             p: 2,
             borderRadius: 2,
             bgcolor: alpha('#ff8ba7', 0.08),
             border: '1px solid',
             borderColor: alpha('#ff8ba7', 0.25),
           }}
         >
           <CustomFrameLayoutPreview
             width={customFrame.width}
             height={customFrame.height}
             size={customFrame.size}
             photoPositions={customFrame.photoPositions}
             maxHeight={{ xs: 220, sm: 300 }}
           />
         </Box>
         
         <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
           <Chip
             label={customFrame.size}
             sx={{
               bgcolor: getSizeColor(customFrame.size),
               color: 'white',
               fontWeight: 'bold'
             }}
           />
           <Chip
             label={`${customFrame.maxPhotos} photos`}
             variant="outlined"
           />
          <Chip
            label={`${userFrames.length} user frames`}
            variant="outlined"
            color="secondary"
          />
          <Chip
            label={`${backgrounds.length} backgrounds`}
            variant="outlined"
            color="info"
          />
         </Box>


      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, value: 'frames' | 'backgrounds') => setActiveTab(value)}
        >
          <Tab value="frames" label={`User Frames (${userFrames.length})`} />
          <Tab value="backgrounds" label={`Backgrounds (${backgrounds.length})`} />
        </Tabs>
      </Box>

      {activeTab === 'frames' ? (
        <>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
              Upload User Frames
            </Typography>
            <Paper
              {...getFrameRootProps()}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                px: 2.5,
                py: 2,
                borderRadius: 2.5,
                border: '1.5px solid',
                borderColor: isFrameDragActive ? alpha('#e86a8a', 0.55) : alpha('#ff8ba7', 0.35),
                background: isFrameDragActive
                  ? `linear-gradient(135deg, ${alpha('#ff8ba7', 0.28)} 0%, ${alpha('#ff8ba7', 0.16)} 100%)`
                  : `linear-gradient(135deg, ${alpha('#ff8ba7', 0.18)} 0%, ${alpha('#ff8ba7', 0.08)} 100%)`,
                cursor: 'pointer',
                transition: 'all 0.22s ease',
                boxShadow: 'none',
                '&:hover': {
                  borderColor: alpha('#e86a8a', 0.45),
                  transform: 'translateY(-1px)',
                },
              }}
            >
              <input {...getFrameInputProps()} />
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha('#ff8ba7', 0.35),
                  color: '#e86a8a',
                  flexShrink: 0,
                }}
              >
                <CloudUpload />
              </Box>
              <Box sx={{ textAlign: 'center', minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, color: '#e86a8a' }}>
                  {isUploadingFrames ? 'Đang upload user frames...' : 'Thêm user frames'}
                </Typography>
                <Typography variant="body2" sx={{ color: alpha('#e86a8a', 0.7), display: { xs: 'none', sm: 'block' } }}>
                  Tối đa 10 files, mỗi file tối đa 5MB
                </Typography>
              </Box>
            </Paper>
          </Box>

          {userFrames.length === 0 ? (
            <Box textAlign="center" py={4}>
              <PhotoIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="textSecondary">
                Chưa có user frames nào cho custom frame này
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Bạn có thể upload frames mới để sử dụng template này
              </Typography>
            </Box>
          ) : (
            <>
              <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
                User Frames ({userFrames.length})
              </Typography>

              <Grid container spacing={3}>
                {userFrames.map((userFrame) => (
                  <Grid item xs={12} sm={6} md={4} key={userFrame._id}>
                    <Card
                      sx={{
                        height: 'auto',
                        position: 'relative',
                        '&:hover .delete-button': {
                          opacity: 1
                        }
                      }}
                    >
                      <CardMedia
                        component="img"
                        height="200"
                        image={userFrame.frameImage?.url || '/placeholder-frame.png'}
                        alt={userFrame.name}
                        sx={{
                          objectFit: 'contain',
                          backgroundColor: '#f5f5f5',
                          padding: '8px'
                        }}
                      />
                      <IconButton
                        className="delete-button"
                        onClick={() => handleDeleteUserFrame(userFrame._id)}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          backgroundColor: 'rgba(0, 0, 0, 0.5)',
                          color: 'white',
                          opacity: 0,
                          transition: 'opacity 0.2s',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.7)'
                          }
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </>
      ) : (
        <>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
              Upload Backgrounds
            </Typography>
            <Paper
              {...getBackgroundRootProps()}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                px: 2.5,
                py: 2,
                borderRadius: 2.5,
                border: '1.5px solid',
                borderColor: isBackgroundDragActive ? alpha('#e86a8a', 0.55) : alpha('#ff8ba7', 0.35),
                background: isBackgroundDragActive
                  ? `linear-gradient(135deg, ${alpha('#ff8ba7', 0.28)} 0%, ${alpha('#ff8ba7', 0.16)} 100%)`
                  : `linear-gradient(135deg, ${alpha('#ff8ba7', 0.18)} 0%, ${alpha('#ff8ba7', 0.08)} 100%)`,
                cursor: 'pointer',
                transition: 'all 0.22s ease',
                boxShadow: 'none',
                '&:hover': {
                  borderColor: alpha('#e86a8a', 0.45),
                  transform: 'translateY(-1px)',
                },
              }}
            >
              <input {...getBackgroundInputProps()} />
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha('#ff8ba7', 0.35),
                  color: '#e86a8a',
                  flexShrink: 0,
                }}
              >
                <CloudUpload />
              </Box>
              <Box sx={{ textAlign: 'center', minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, color: '#e86a8a' }}>
                  {isUploadingBackgrounds ? 'Đang upload background...' : 'Thêm background (1 ảnh)'}
                </Typography>
                <Typography variant="body2" sx={{ color: alpha('#e86a8a', 0.85), fontWeight: 700 }}>
                  Kích thước khuyến nghị: {firstSlotSize.width}×{firstSlotSize.height} px
                </Typography>
                <Typography variant="body2" sx={{ color: alpha('#e86a8a', 0.7), display: { xs: 'none', sm: 'block' } }}>
                  Chọn 1 ảnh → căn chỉnh · tối đa 5MB
                </Typography>
              </Box>
            </Paper>
          </Box>

          {backgrounds.length === 0 ? (
            <Box textAlign="center" py={4}>
              <PhotoIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="textSecondary">
                Chưa có background nào cho custom frame này
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Bạn có thể upload backgrounds riêng cho custom frame này
              </Typography>
            </Box>
          ) : (
            <>
              <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
                Backgrounds ({backgrounds.length})
              </Typography>

              <Grid container spacing={3}>
                {backgrounds.map((background) => (
                  <Grid item xs={12} sm={6} md={4} key={background._id}>
                    <Card
                      sx={{
                        height: 'auto',
                        position: 'relative',
                        '&:hover .delete-background-button': {
                          opacity: 1
                        }
                      }}
                    >
                      <CardMedia
                        component="img"
                        height="200"
                        image={background.frameImage?.url || '/placeholder-frame.png'}
                        alt={background.name}
                        sx={{
                          objectFit: 'contain',
                          backgroundColor: '#f5f5f5',
                          padding: '8px'
                        }}
                      />
                      <IconButton
                        className="delete-background-button"
                        onClick={() => handleDeleteBackground(background._id)}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          backgroundColor: 'rgba(0, 0, 0, 0.5)',
                          color: 'white',
                          opacity: 0,
                          transition: 'opacity 0.2s',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.7)'
                          }
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </>
      )}

      <SlotAspectCropDialog
        open={bgCropOpen}
        sourceFile={bgCropFile}
        slotWidth={firstSlotSize.width}
        slotHeight={firstSlotSize.height}
        title="Căn chỉnh ảnh nền theo ô slot"
        hint="Kéo để dịch ảnh, zoom để phóng to. Ảnh sẽ được cắt đúng tỉ lệ ô slot đầu của khung trước khi upload."
        confirmLabel="Cắt & upload"
        onClose={() => {
          if (isUploadingBackgrounds) return;
          setBgCropOpen(false);
          setBgCropFile(null);
        }}
        onConfirm={uploadCroppedBackground}
      />
    </Container>
  );
};

export default CustomFrameDetail;
